import React, {
	SetStateAction,
	useMemo,
	useState,
} from 'react';
import {
	Grid,
} from 'antd';
import { asyncForEach } from '@shared/utils/array';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import { useCargoHandlers } from '@client/screens/estimates/details/context/hooks/useCargoHandlers';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';
import { updateCargoPort } from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import { getLoadedAndDischarged } from '@client/screens/cargos/details/utils/CargoScreenUtil';
import Modal from '@client/components/Modal/Modal';
import { ItineraryCargoPortsDetails } from './ItineraryCargoPortsDetails';

type Props = {
	cargoId: number;
	cargoPortId: number | null;
	visible: boolean;
	refresh?: () => Promise<void>;
	setEditingCargoPortId?: React.Dispatch<SetStateAction<number | null>>;
	updateInProgress?: boolean;
	handleClose?: () => void;
};

const ItineraryCargoDetailsModal = ({
	cargoId,
	cargoPortId,
	visible,
	refresh,
	setEditingCargoPortId,
	handleClose,
}: Props) => {
	const [saving, setSaving] = useState(false);
	const screens = Grid.useBreakpoint();

	const onClose = async () => {
		await refresh?.();

		setEditingCargoPortId?.(null);
		handleClose?.();
	};

	const {
		makeBlockable,
	} = useNavigationBlock();

	const onCloseModal = makeBlockable(onClose);

	const {
		cargo: cargoDetails,
		cargoPorts,
		onUpdateCargoPort,
		hasPendingChanges,
		pendingCargoPortChanges,
		setPendingCargoPortChanges,
		loading,
	} = useCargoHandlers({
		cargoId,
		selectedEstimateId: null,
	});

	const { loaded, discharged } = useMemo(() => {
		if (cargoDetails != null && cargoDetails.cargoPorts != null) {
			return getLoadedAndDischarged(cargoDetails.cargoPorts);
		}

		return {
			loaded: 0,
			discharged: 0,
		};
	}, [cargoDetails]);

	const quantitiesRemaining = useMemo(() => {
		if (cargoDetails == null) {
			return null;
		}

		const loadedRemaining = cargoDetails.quantity - loaded;
		const dischargeRemaining = loaded - discharged;

		return {
			loadedRemaining,
			dischargeRemaining,
		};
	}, [cargoDetails, discharged, loaded]);

	const onSaveChanges = async () => {
		setSaving(true);

		try {
			await asyncForEach(pendingCargoPortChanges, async (c) => {
				await updateCargoPort(c, false);
			});
			setPendingCargoPortChanges([]);
			showSuccessNotification('Cargo-port updated');
		} catch (e) {
			showErrorNotification('Could not update cargo-port', e as Error);
		} finally {
			setSaving(false);
		}
	};

	const saveButton = useSaveButton({
		onSave: onSaveChanges,
		title: 'Save & Calculate',
		loading: loading || saving,
		hasPendingChanges: hasPendingChanges,
	});

	return (
		<Modal
			closable={false}
			open={visible}
			onCancel={onCloseModal}
			destroyOnClose
			width="calc(80vw - 6%)"
			okText="Close"
			okButtonProps={{
				onClick: () => onCloseModal(),
			}}
			styles={{ body: { padding: screens.lg ? 5 : 0 } }}
			cancelButtonProps={{
				style: {
					visibility: 'hidden',
				},
			}}
			footer={saveButton}
		>
			<ItineraryCargoPortsDetails
				handleChange={onUpdateCargoPort}
				cargoPorts={(cargoPorts ?? []).filter((cp) => cp.id === cargoPortId)}
				cargoDetails={cargoDetails}
				quantitiesRemaining={quantitiesRemaining}
			/>
		</Modal>
	);
};

export default ItineraryCargoDetailsModal;
